import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Combobox, Dialog, DialogContent, DialogDescription, DialogTitle } from '@onward-delivery/design';
import * as ROUTES from '@/constants/routes';
import ProductList from './ProductList';
import OrderSummary from './OrderSummary';
import { ALLOCATION_MODES } from './constants';
import { buildPlaceholderItems, draftBlockedReason, isDraftComplete, serialEmptyText } from './utils';
import { useCarrierPickupLocation, useOrderDraft, useSellableSkuItems, useSellableSkus } from './hooks';

const CreateOrderModal = ({ open, onOpenChange }) => {
    const navigate = useNavigate();
    const { rows, loading, error } = useSellableSkus({ enabled: open });
    const { draft, add, setQty, setSerials, remove, clear } = useOrderDraft();
    const [carrierId, setCarrierId] = useState('');
    const [allocationMode, setAllocationMode] = useState(ALLOCATION_MODES.FIFO);
    const {
        location: pickupLocation,
        carrierPhone,
        loading: pickupLoading,
        error: pickupError,
    } = useCarrierPickupLocation(carrierId);
    const carrierName = rows.find((row) => row.carrier_id === carrierId)?.carrier_name || null;

    // Lives here rather than in ProductList because the picker sits in the
    // header now: the list is downstream of the choice, not the owner of it.
    const carrierOptions = useMemo(() => {
        const byId = new Map();
        rows.forEach((row) => {
            if (row.carrier_id && !byId.has(row.carrier_id)) {
                byId.set(row.carrier_id, { value: row.carrier_id, label: row.carrier_name || row.carrier_id });
            }
        });
        return Array.from(byId.values());
    }, [rows]);

    const handleCarrierChange = useCallback(
        (next) => {
            if (next !== carrierId) {
                clear();
            }
            setCarrierId(next);
        },
        [carrierId, clear]
    );

    const handleModeChange = useCallback(
        (next) => {
            if (next !== allocationMode) {
                clear();
            }
            setAllocationMode(next);
        },
        [allocationMode, clear]
    );

    const handleAdd = useCallback(
        (row) => add(row, { qty: allocationMode === ALLOCATION_MODES.SERIALIZED ? 0 : 1 }),
        [add, allocationMode]
    );

    // With a single carrier there is no choice to make, so make it.
    useEffect(() => {
        if (!carrierId && carrierOptions.length === 1) {
            handleCarrierChange(carrierOptions[0].value);
        }
    }, [carrierOptions, carrierId, handleCarrierChange]);

    const draftSkus = useMemo(
        () => Array.from(new Set(Array.from(draft.values()).map((line) => line.sku))).sort(),
        [draft]
    );

    const {
        itemsByKey,
        loaded: itemsLoaded,
        error: itemsError,
    } = useSellableSkuItems(carrierId, draftSkus, {
        enabled: open && allocationMode === ALLOCATION_MODES.SERIALIZED,
    });

    const carrierBlocked = !carrierId || (pickupLoading && !pickupLocation) || !!pickupError;
    const createDisabled = carrierBlocked || !isDraftComplete(draft, allocationMode, { itemsLoaded });
    const disabledReason = carrierBlocked
        ? null
        : draftBlockedReason(draft, allocationMode, { itemsLoaded, itemsError });

    const handleCreate = () => {
        navigate(ROUTES.IMPORT_ORDERS, {
            state: {
                forwardStocking: {
                    carrier_id: carrierId,
                    carrier_name: carrierName,
                    carrier_phone: carrierPhone,
                    pickup_location: pickupLocation,
                    items: buildPlaceholderItems(draft, itemsByKey),
                },
            },
        });
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(next) => {
                if (!next) {
                    clear();
                    setCarrierId('');
                    setAllocationMode(ALLOCATION_MODES.FIFO);
                }
                onOpenChange(next);
            }}
        >
            <DialogContent className="w-[956px] max-w-[95vw] overflow-hidden rounded-2xl p-0">
                <div className="flex items-center justify-between gap-4 border-b border-border px-8 pb-5 pt-6">
                    <div className="flex flex-col items-start">
                        <DialogTitle className="text-xl font-bold text-foreground">Create order</DialogTitle>
                        <DialogDescription className="text-[13px] text-muted">
                            Search sellable stock and add SKUs to the order
                        </DialogDescription>
                    </div>
                    {/* mr-6 keeps clear of DialogContent's own close button, which
                        is pinned at right-4 regardless of what the header holds. */}
                    <Combobox
                        className="mr-6 w-72 shrink-0"
                        options={carrierOptions}
                        value={carrierId}
                        onValueChange={handleCarrierChange}
                        placeholder="Select a carrier"
                        emptyText="No carriers"
                    />
                </div>
                {/* Fixed height, as in the design: the panels would otherwise
                    grow and shrink as stock is filtered, and the footer button
                    would walk up the screen mid-task. */}
                <div className="flex h-[min(70vh,786px)]">
                    <ProductList
                        rows={rows}
                        loading={loading}
                        error={error}
                        draft={draft}
                        mode={allocationMode}
                        onAdd={handleAdd}
                        onQtyChange={setQty}
                        carrierId={carrierId}
                        needsCarrier={!carrierId && carrierOptions.length > 1}
                    />
                    <OrderSummary
                        draft={draft}
                        mode={allocationMode}
                        onModeChange={handleModeChange}
                        itemsByKey={itemsByKey}
                        onQtyChange={setQty}
                        onSerialsChange={setSerials}
                        onRemove={remove}
                        onCreate={handleCreate}
                        disabled={createDisabled}
                        disabledReason={disabledReason}
                        emptyText={serialEmptyText({ itemsLoaded, itemsError })}
                    />
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default CreateOrderModal;
